import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { Send, Sparkles, TrendingUp } from 'lucide-react';
import Navigation from '@/components/Navigation';
import confetti from 'canvas-confetti';

function RoleplaySimulator() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [scenario, setScenario] = useState('');
  const [context, setContext] = useState('');
  const [showSetup, setShowSetup] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scenarios = [
    'Negotiate a 10% salary raise with your boss',
    'Reduce monthly utility bills by calling provider',
    'Get a better price on a car purchase',
    'Negotiate higher freelance project rates',
    'Resolve a family dispute over responsibilities'
  ];

  const startRoleplay = async () => {
    if (!scenario) {
      toast.error('Please select or enter a scenario');
      return;
    }

    try {
      const response = await API.post('/roleplay/start', { scenario, context });
      setSessionId(response.data.session_id);
      setShowSetup(false);
      setMessages([
        { role: 'system', content: `Scenario: ${scenario}. You are now in a negotiation. Start by introducing your request.` }
      ]);
      toast.success('Roleplay started!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to start roleplay');
      }
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await API.post('/roleplay/message', {
        session_id: sessionId,
        message: userMessage
      });

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: response.data.ai_message },
        { role: 'coaching', content: response.data.coaching_tip }
      ]);

      if (response.data.is_complete) {
        setSessionComplete(true);
        setScore(response.data.score);
        setFeedback(response.data.feedback);
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (showSetup) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Navigation />
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <Target className="text-green-500 w-16 h-16 mx-auto mb-4" />
            <h1 className="font-['Outfit'] text-4xl font-bold mb-3" data-testid="roleplay-title">AI Roleplay Simulator</h1>
            <p className="text-gray-400">Practice real negotiations with AI feedback</p>
          </div>

          <div className="ninja-card">
            <h2 className="font-['Outfit'] text-2xl font-bold mb-4">Choose Your Scenario</h2>
            <div className="space-y-3 mb-6">
              {scenarios.map(s => (
                <button
                  key={s}
                  onClick={() => setScenario(s)}
                  className={`w-full p-4 rounded-lg text-left border transition-all ${
                    scenario === s ? 'border-green-500 bg-green-500/10' : 'border-white/10 hover:bg-white/5'
                  }`}
                  data-testid={`scenario-option-${s.substring(0, 20).replace(/ /g, '-').toLowerCase()}`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">Or enter your own scenario:</label>
              <Input
                value={scenario}
                onChange={(e) => setScenario(e.target.value)}
                placeholder="e.g., Negotiate rent reduction with landlord"
                className="bg-black/50 border-white/20"
                data-testid="custom-scenario-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">Additional context (optional):</label>
              <Input
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g., Boss is budget-conscious, industry avg is 15% higher"
                className="bg-black/50 border-white/20"
                data-testid="scenario-context-input"
              />
            </div>

            <Button 
              onClick={startRoleplay} 
              className="w-full bg-green-600 hover:bg-green-500"
              data-testid="start-simulation-button"
            >
              Start Simulation
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <Navigation />
      
      <div className="flex-1 max-w-4xl mx-auto w-full px-6 py-8 flex flex-col">
        <div className="mb-6" data-testid="roleplay-header">
          <h2 className="font-['Outfit'] text-2xl font-bold">Scenario: {scenario}</h2>
          {sessionComplete && (
            <div className="mt-4 p-4 bg-green-500/20 border border-green-500/40 rounded-lg" data-testid="roleplay-complete">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="text-yellow-500 w-6 h-6" />
                <span className="font-bold text-xl">Score: {score}/100</span>
              </div>
              <p className="text-gray-300">{feedback}</p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto mb-6 space-y-4" data-testid="chat-messages">
          {messages.map((msg, idx) => (
            <div key={idx}>
              {msg.role === 'user' && (
                <div className="flex justify-end">
                  <div className="chat-bubble-user" data-testid="user-message">{msg.content}</div>
                </div>
              )}
              {msg.role === 'assistant' && (
                <div className="flex justify-start">
                  <div className="chat-bubble-ai" data-testid="ai-message">{msg.content}</div>
                </div>
              )}
              {msg.role === 'coaching' && (
                <div className="flex justify-center">
                  <div className="text-sm bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 px-4 py-2 rounded-full" data-testid="coaching-tip">
                    <Sparkles className="inline w-4 h-4 mr-2" />{msg.content}
                  </div>
                </div>
              )}
              {msg.role === 'system' && (
                <div className="text-center text-sm text-gray-500 italic" data-testid="system-message">{msg.content}</div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {!sessionComplete && (
          <div className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your response..."
              className="flex-1 bg-black/50 border-white/20 h-12"
              disabled={loading}
              data-testid="message-input"
            />
            <Button 
              onClick={sendMessage} 
              disabled={loading || !input.trim()}
              className="bg-green-600 hover:bg-green-500 px-6"
              data-testid="send-message-button"
            >
              {loading ? 'Sending...' : <Send className="w-5 h-5" />}
            </Button>
          </div>
        )}

        {sessionComplete && (
          <Button 
            onClick={() => window.location.reload()} 
            className="w-full bg-green-600 hover:bg-green-500"
            data-testid="new-roleplay-button"
          >
            Start New Roleplay
          </Button>
        )}
      </div>
    </div>
  );
}

export default RoleplaySimulator;

import { Target } from 'lucide-react';